import {
  TextField,
  TextInput,
  SelectField,
  SelectInput,
  BooleanInput,
  NumberField,
  useNotify,
  useTheme,
  useDataProvider,
  TopToolbar,
  useRecordContext,
  Link,
  BulkUpdateWithConfirmButton,
  useGetIdentity,
  WrapperField,
  WithRecord,
  useResourceContext,
} from "react-admin";
import { useParams } from "react-router-dom";
import LockIcon from "@mui/icons-material/Lock";
import { Edit as EditIcon } from "@mui/icons-material";
import {
  errorDisplayConfig,
  getErrorMessage,
} from "../ra-lb-tools/utils/errors";
import { useEffect, useState } from "react";
import { LocationInput } from "./locations/components";
import { RecordSummary, MyShow } from "../ra-lb-tools/components/MyShow";
import { ReservationList } from "./reservations";
import { MyList } from "../ra-lb-tools/components/myList";
import { MyEdit } from "../ra-lb-tools/components/myMutator";
import {
  OpenLockerTokenClientInfo,
  OpenLockerTokenList as OpenLockerTokenList,
} from "./openLockerTokens";
import { LockerHistoryList } from "./lockerHistory";
import {
  ActiveLockerUsageList,
  LockerUsageList,
  LockerUsageTime,
} from "./billing/lockerUsage";
import { MyBooleanField as BooleanField } from "../components/myBooleanField";
import { LockerStateList } from "./lockerState";
import CommandsList from "./commands";
import EventsList from "./events";
import { Column } from "../ra-lb-tools/components/column";
import { Row } from "../ra-lb-tools/components/row";
import { Box, IconButton } from "@mui/material";
import { status_icon } from "./tower_render/lockerIcons";
import get from "lodash/get";
import { GoBackButton } from "../ra-lb-tools/components/goBackButton";
import { LabeledColumn } from "../ra-lb-tools/components/labeledColumn";
import { MyEditButton as EditButton } from "../ra-lb-tools/components/myEditButton";
import {
  MyTabbedShowLayout,
  Tab,
} from "../ra-lb-tools/components/myTabbedShowLayout";
import { LockerRequestList } from "./lockerRequest";
import { LockerOpenRequestList } from "./lockerOpenRequest";
import { LockerUsageEventList } from "./billing/lockerUsageEvent";
import { LastReferenceField } from "../ra-lb-tools/components/lastReferenceField";
import { MyDateField } from "../ra-lb-tools/components/myDateField";
import { getEnvStyling, humanDateDiff } from "../ra-lb-tools/utils/tools";
import { MyLinkField } from "../ra-lb-tools/components/myLinkField";
import { Debug } from "../ra-lb-tools/components/debug";
import { UserField, UserInput } from "./users";
import { TowerField } from "./tower/towers";
import { NullableBooleanField } from "../ra-lb-tools/components/nullableBooleanField";
import { LinkButton } from "../ra-lb-tools/components/linkButton";
import { Link as MuiLink } from "@mui/material";
import { WithRoute } from "../ra-lb-tools/acl/withRoute";
import { LockerStatusInput } from "./lockerStatus";
import { formatLockerStatus } from "../utils/formatLockerStatus";
import { LockerTypeField, LockerTypeInput } from "./lockerTypes";
import { useMyContext } from "../ra-lb-tools/components/myContext";
import { WithPermissions } from "../ra-lb-tools/acl/withPermissions";
import { getValidResource } from "../ra-lb-tools/acl/tools";

export const LockerIcon = LockIcon;

export const accessModeChoices = [
  { id: 1, name: "Public" },
  { id: 2, name: "Restricted" },
  { id: 3, name: "Internal Use" },
];

const lockerFilters = () => [
  <TextInput source="towerId" alwaysOn key={1} />,
  <LockerStatusInput source="statusId" alwaysOn key={2} />,
  <LockerTypeInput source="typeId" key="typeId" alwaysOn />,
  <LocationInput source="tower__location_id" allowEmpty key={3} />,
  <BooleanInput source="isLowLocker" key={4} />,
  <UserInput
    source="assignedUserId"
    label="Assigned to"
    allowEmpty
    alwaysOn
    key={3}
  />,
  <NullableBooleanField source="isForTest" alwaysOn key={5} />,
  <NullableBooleanField source="isOnline" key={"isOnline"} alwaysOn />,
  <NullableBooleanField
    source="hasOnlineHardware"
    key={"hasOnlineHardware"}
    alwaysOn
  />,
];

const rowStyle = (record, palette) => {
  return {
    backgroundColor:
      record.statusId == 4
        ? palette.specialColors.oos
        : palette.background.paper,
  };
};

const BulkStatusChangeButtons = ({
  statusChoices,
  currentUserId,
  meta = {
    customSegment: "status",
  },
  ...rest
}) => {
  const sx = {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    position: "absolute",
    top: "0",
    textAlign: "left",
    padding: "10px",
    alignItems: "baseline",
    border: "1px solid black",
    "> *": {
      padding: "5px 0 !important",
      width: "100%",
      justifyContent: "flex-start !important",
    },
  };

  return (
    <Box sx={sx}>
      {statusChoices.length ? (
        statusChoices.map(({ id, name, isAssignable }) => (
          <BulkUpdateWithConfirmButton
            key={id}
            label={"Set " + name}
            data={{
              status_id: id,
              assignedUserId: isAssignable ? currentUserId : null,
              __meta: meta,
            }}
            icon={status_icon[id]?.icon}
            {...rest}
          />
        ))
      ) : (
        <Box>No status choices</Box>
      )}
    </Box>
  );
};

export const LockersStatusField = ({
  record,
  statusIdField = "statusId",
  editable = true,
  sortable: _sortable,
  showColors = false,
  ...props
}) => {
  record = record || useRecordContext();
  const sx = { display: "flex", alignItems: "center" };
  const status_id = get(record, statusIdField);
  const status_info = status_icon[status_id];

  if (showColors) {
    sx.color = status_info.color;
  }

  return (
    <Box sx={sx}>
      {status_info?.icon}
      <span {...props} style={{ marginLeft: "5px" }}>
        {status_info?.label}
      </span>
      {editable ? <EditStatusButton iconOnly={true} /> : null}
    </Box>
  );
};

export const OccupiedLockerClientInfo = ({ source: _source, ...props }) => {
  return (
    <LastReferenceField
      reference="open-locker-tokens"
      filter={{ intent: 1 }}
      source="locker_id"
      render={(olt) => (
        <>
          <OpenLockerTokenClientInfo record={olt} />
          <Debug>
            (#
            <MyLinkField
              record={olt}
              source="id"
              resource="open-locker-tokens"
            />
            )
          </Debug>
        </>
      )}
      {...props}
    />
  );
};

export const LockerList = (props) => {
  const [theme] = useTheme();
  const palette = theme.palette;

  const { acp } = useMyContext();
  const statusChoices = getLockerStatusChoices(false, acp);
  const currentUserId = get(useGetIdentity(), "data.id");

  const resource = "lockers";

  return (
    <Row>
      <Column>
        <MyList
          resource={resource}
          filters={lockerFilters()}
          dataGridProps={{
            rowStyle: (record) => rowStyle(record, palette),
            bulkActionButtons: acp.can({
              resource: getValidResource("lockers"),
              action: "update_status",
            }) ? (
              <BulkStatusChangeButtons
                statusChoices={statusChoices.filter((st) => st.id == 1)}
                currentUserId={currentUserId}
              />
            ) : null,
            omit: ["last_usage"],
          }}
          {...props}
        >
          <TextField source="id" />
          <TextField source="name" />
          <LockersStatusField
            source="status.name"
            label="Status"
            sortable={false}
          />
          <UserField source="assignedUserId" />
          <WrapperField label="Last usage" source="last_usage">
            <LastReferenceField
              reference="locker-usage-events"
              source="locker_id"
              render={(record) => (
                <>
                  <MyDateField record={record} source="timestamp" showTime />
                  <span> ({humanDateDiff(null, record.timestamp)} ago)</span>
                </>
              )}
            />
          </WrapperField>
          <WrapperField label="Active Usage Time" source="activeUsageTime">
            <LastReferenceField
              label="Active Usage Time"
              reference="locker-usage"
              source="locker_id"
              filter={{ active: true }}
              render={(lockerUsage) => (
                <Link to={`/locker-usage/${lockerUsage.id}/show`}>
                  <LockerUsageTime lockerUsage={lockerUsage} />
                </Link>
              )}
            />
          </WrapperField>
          <OccupiedLockerClientInfo
            source="occupiedLockerClientInfo"
            label="Last Drop off client info"
            validateRecord={(record) => record.statusId === 2}
          />
          <LockerTypeField source="typeId" label="Type" />
          <SelectField source="accessModeId" choices={accessModeChoices} />
          <BooleanField source="isLowLocker" />
          <NumberField source="physicalId" label="Port" />
          <BooleanField source="isForTest" sortable={false} />
          <TowerField source="towerId" />
          <TextField source="keypadCode" />
          <BooleanField source="keypadCodePersists" />
          <WrapperField
            label="Last History Entry"
            source="last_history"
            sortable={false}
          >
            <LastHistoryEntry />
          </WrapperField>
        </MyList>
      </Column>
    </Row>
  );
};

LockerList.defaultProps = {
  resource: "lockers",
};

const locker_status_choices = {
  true: [],
  false: [],
};

const getLockerStatusChoices = (verbose, acp) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const verbose_key = `${verbose}`;
  const [statusChoices, setStatusChoices] = useState(
    locker_status_choices[verbose_key]
  );

  useEffect(() => {
    if (
      !statusChoices.length &&
      acp.can({ resource: "locker-statuses", action: "read_all" })
    ) {
      dataProvider
        .retrieveLockerStatuses()
        .then((res) => {
          locker_status_choices[verbose_key] = res["data"].map((st) =>
            formatLockerStatus(st, verbose)
          );
          setStatusChoices(locker_status_choices[verbose_key]);
        })
        .catch((err) =>
          notify(
            `Error getting locker status: ${getErrorMessage(err)}`,
            errorDisplayConfig
          )
        );
    }
  }, []);

  if (!statusChoices.length) {
    console.warn(`getLockerStatusChoices: no status choices found`);
  }

  return statusChoices;
};

const validateLockerAssignability = (values, statusChoices) => {
  const errors = {};

  const statusId = values.statusId;
  const status = statusChoices.find((choice) => choice["id"] == statusId);

  if (!status) {
    errors.statusId = "Status not found";
  }

  const non_assignable = status && !status.isAssignable;

  if (non_assignable && values.assignedUserId) {
    errors.assignedUserId =
      "Non assignable status, assigned user should be empty";
  }

  if (!(non_assignable || values.assignedUserId)) {
    errors.assignedUserId = "ra.validation.required";
  }

  console.log(errors);

  return errors;
};

export const LockerEdit = (props) => {
  const { acp } = useMyContext();

  const statusChoices = getLockerStatusChoices(true, acp);

  return (
    <MyEdit
      {...props}
      formProps={{
        validate: (values) =>
          validateLockerAssignability(values, statusChoices),
        shouldUnregister: true,
      }}
    >
      <TextInput source="id" disabled />
      <TextInput source="name" />
      <BooleanInput source="isLowLocker" />
      <TextInput source="towerId" disabled />
      <TextInput source="type.name" disabled />
      <SelectInput
        source="accessModeId"
        choices={accessModeChoices}
        optionValue="id"
        optionText="name"
      />
      <SelectInput
        source="statusId"
        choices={statusChoices}
        optionValue="id"
        optionText="name"
      />
      <UserInput source="assignedUserId" allowEmpty />
    </MyEdit>
  );
};

const KeypadCodePersists = () => {
  const record = useRecordContext();
  if (!record) return null;
  const allowed = record.accessModeId != 1;

  return allowed && <BooleanInput source="keypadCodePersists" />;
};

export const LockerKeypadEdit = (props) => {
  return (
    <MyEdit
      {...props}
      formProps={{
        shouldUnregister: true,
      }}
      mutationOptions={{ meta: { customSegment: "keypad" } }}
    >
      <TextInput source="id" disabled />
      <TextInput source="name" disabled />
      <KeypadCodePersists source="keypadCodePersists" />
      <TextInput source="keypadCode" />
    </MyEdit>
  );
};

const EditKeypadButton = () => {
  return (
    <WithRoute
      route={":id/keypad"}
      label="EditKeypadButton"
      render={() => {
        const record = useRecordContext();

        if (!record) return null;
        const path = `/lockers/${record.id}/keypad`;

        return (
          <LinkButton
            to={path}
            label={"Keypad"}
            onClick={(e) => e.stopPropagation()}
          >
            <EditIcon />
          </LinkButton>
        );
      }}
    />
  );
};

export const LockerStatusEdit = (props) => {
  const resource = useResourceContext();
  const { acp } = useMyContext();
  const statusChoices = getLockerStatusChoices(true, acp);

  return (
    <MyEdit
      {...props}
      formProps={{
        validate: (values) =>
          resource === "org-lockers" ||
          validateLockerAssignability(values, statusChoices),
        shouldUnregister: true,
      }}
      mutationOptions={{ meta: { customSegment: "status" } }}
      permissionAction="update_status"
    >
      <TextInput source="id" disabled />
      <TextInput source="name" disabled />
      <LockerStatusInput source="statusId" verbose={true} />
      <UserInput source="assignedUserId" allowEmpty />
    </MyEdit>
  );
};

const EditStatusButton = ({ iconOnly, ...props }) => {
  return (
    <WithPermissions
      can={{ resource: getValidResource("lockers"), action: "update_status" }}
    >
      <WithRoute
        route={":id/status"}
        label="EditStatusButton"
        render={() => {
          const resource = useResourceContext();
          const record = useRecordContext();

          if (!record) return null;
          let path = `/${resource}/${record.id}/status`;
          let Component = LinkButton;
          let componentProp = null;

          if (iconOnly) {
            Component = IconButton;
            componentProp = MuiLink;
            path = getEnvStyling().root + path;
          }

          return (
            <Component
              component={componentProp}
              href={path}
              label={"Status"}
              onClick={(e) => e.stopPropagation()}
              title={"Edit status"}
              sx={{ marginLeft: "5px" }}
              {...props}
            >
              <EditIcon />
            </Component>
          );
        }}
      />
    </WithPermissions>
  );
};

const LastHistoryEntry = () => {
  return (
    <WithRecord
      render={(locker) => (
        <LastReferenceField
          reference="locker-history"
          source="locker_id"
          render={(lockerHistory) => (
            <>
              <Link to={`/lockers/${locker.id}/show/history`}>
                <MyDateField
                  record={lockerHistory}
                  source="timestamp"
                  showTime
                />
              </Link>
            </>
          )}
        />
      )}
    />
  );
};

const LockerShowActions = () => (
  <TopToolbar>
    <EditButton />
    <EditKeypadButton />
    <EditStatusButton />
    <GoBackButton size="small" variant="text" />
  </TopToolbar>
);

export const LockerShow = (props) => {
  const { id } = useParams();
  const tabFilter = { lockerId: parseInt(id) };

  return (
    <MyShow {...props} actions={<LockerShowActions />}>
      <MyTabbedShowLayout>
        <Tab label="Summary" can={true}>
          <RecordSummary>
            <LabeledColumn>
              <TextField source="id" />
              <TextField source="name" />
              <TextField source="type.name" label="Type" />
              <SelectField source="accessModeId" choices={accessModeChoices} />
              <LockersStatusField
                source="statusId"
                statusIdField="statusId"
                editable={false}
              />
            </LabeledColumn>
            <LabeledColumn>
              <TowerField source="towerId" />
              <WrapperField label="Active Usage Time" source="activeUsageTime">
                <LastReferenceField
                  label="Active Usage Time"
                  reference="locker-usage"
                  source="locker_id"
                  filter={{ active: true }}
                  render={(lockerUsage) => (
                    <Link to={`/locker-usage/${lockerUsage.id}/show`}>
                      <LockerUsageTime
                        lockerUsage={lockerUsage}
                        sx={{ justifyContent: "flex-start" }}
                      />
                    </Link>
                  )}
                />
              </WrapperField>
              <UserField source="assignedUserId" />
              <OccupiedLockerClientInfo
                source="occupiedLockerClientInfo"
                label="Last Drop off client info"
                validateRecord={(record) => record.statusId === 2}
              />
              <WrapperField label="Last History Entry" source="last_history">
                <LastHistoryEntry />
              </WrapperField>
            </LabeledColumn>
            <LabeledColumn>
              <BooleanField source="isLowLocker" />
              <BooleanField source="isForTest" sortable={false} />
              <TextField source="keypadCode" />
              <BooleanField source="keypadCodePersists" />
            </LabeledColumn>
          </RecordSummary>
        </Tab>
        <Tab
          label="Reservations"
          path="reservations"
          can={{ action: "list", resource: "reservations" }}
        >
          <ReservationList filter={tabFilter} />
        </Tab>
        <Tab
          label="Open Locker Token"
          path="open-locker-tokens"
          can={{ action: "list", resource: "open-locker-token" }}
        >
          <OpenLockerTokenList filter={tabFilter} />
        </Tab>
        <Tab
          label="Open Request"
          path="open-request"
          can={{ action: "list", resource: "locker-open-requests" }}
        >
          <LockerOpenRequestList filter={tabFilter} />
        </Tab>
        <Tab
          label="History"
          path="history"
          can={{ action: "list", resource: "locker_history" }}
        >
          <LockerHistoryList filter={tabFilter} />
        </Tab>
        <Tab
          label="Federation Requests"
          path="federation-requests"
          can={{ action: "list", resource: "locker-request" }}
        >
          <LockerRequestList filter={tabFilter} />
        </Tab>
        <Tab
          label="Active Usage"
          path="active-usage"
          can={{ action: "list", resource: "locker-usage" }}
        >
          <ActiveLockerUsageList filter={tabFilter} />
        </Tab>
        <Tab
          label="Usage"
          path="usage"
          can={{ action: "list", resource: "locker-usage" }}
        >
          <LockerUsageList filter={tabFilter} />
        </Tab>
        <Tab
          label="Usage Events"
          path="usage-events"
          can={{ action: "list", resource: "locker-usage-events" }}
        >
          <LockerUsageEventList filter={{ lockerId: id }} />
        </Tab>
        <Tab
          label="States"
          path="states"
          can={{ action: "list", resource: "locker-state" }}
        >
          <LockerStateList filter={tabFilter} />
        </Tab>
        <Tab
          label="Commands"
          path="commands"
          can={{ action: "list", resource: "commands" }}
        >
          <CommandsList filter={tabFilter} />
        </Tab>
        <Tab
          label="Events"
          path="events"
          can={{ action: "list", resource: "events" }}
        >
          <EventsList filter={tabFilter} />
        </Tab>
      </MyTabbedShowLayout>
    </MyShow>
  );
};
